import { Storage } from './Storage';
import { Transport } from './Transport';
import { DataTransferScheduler, DataTransferSchedulerConfig } from './DataTransferScheduler';
import { Event, EventCollector } from './EventCollector';
import { Span, SpanContext } from './Span';
import { Device, DeviceInput } from './Device';
import { sendFetchStrategy } from './httpRequest';
import { idbKeyvalPersistStore } from './DevicePersist';
import { ActivitySessionManager, ActivitySessionManagerConfig } from './ActivitySessionManager';
import { ActionContext, XApiActionData, XApiActionType } from '@ui/utils/ActivityManager/Action';
import { ActivitySession } from './ActivitySession';
import { EventProcessor } from '@ui/utils/ActivityManager/EventProcessor';
import { ViewManager, ViewManagerParameters } from './ViewManager';
import { View, ViewParameters } from './View';
import { User } from './User';
import isSSR from '@shared/utils/isSSR';
import { StudyEventLogInput, StudyEventAndSessionRequestInput } from './type';
import { activitySessionManagerPersist } from './ActivitySessionManagerPersist';
import delay from '@shared/utils/delay';
import { APP_SESSION_EVENTS, LRS_X_API_EVENTS } from './constants';

export type ActivityManagerConfig = {
  deviceConfig?: DeviceInput;
  sessionConfig?: Omit<
    ActivitySessionManagerConfig,
    'onActivitySessionFinish' | 'onActivitySessionStart' | 'canRegenerate' | 'persistStore'
  >;
  dataTransferSchedulerConfig?: Omit<DataTransferSchedulerConfig, 'onSend'>;
  viewManagerConfig?: Omit<ViewManagerParameters, 'onViewChange'>;
};

type EventAddedListner = (event: Event) => void;

export class ActivityManager {
  storage: Storage<StudyEventAndSessionRequestInput>;
  transport: Transport<StudyEventLogInput>;
  dataTransferScheduler: DataTransferScheduler;
  eventCollector: EventCollector;
  device: Device;
  activitySessionManager: ActivitySessionManager;
  eventProcessor: EventProcessor;
  viewManager: ViewManager;
  currentUser: User;
  eventAddedListeners: EventAddedListner[] = [];

  constructor(config?: ActivityManagerConfig) {
    this.storage = new Storage();
    this.viewManager = new ViewManager({
      onViewChange: this.#handleViewChange,
      ...(config?.viewManagerConfig || { initialView: { path: '/' } }),
    });
    this.transport = new Transport({
      sendStrategy: async (payload, onResponse) => {
        await sendFetchStrategy(payload);
      },
    });
    this.dataTransferScheduler = new DataTransferScheduler({
      ...config?.dataTransferSchedulerConfig,
      onSend: this.#sendData,
    });
    this.eventCollector = new EventCollector({
      onEventEnd: this.onEventEnd,
    });
    this.activitySessionManager = new ActivitySessionManager({
      ...config?.sessionConfig,
      persistStore: activitySessionManagerPersist,
      onActivitySessionFinish: this.#onActivitySessionFinish,
      onActivitySessionStart: this.#onActivitySessionStart,
      canRegenerate: (session) => {
        if (this.eventCollector.size() === 0) {
          return true;
        }
        const hasInProgressSpan = this.eventCollector.find(
          (span) => span.activitySessionId === session.activitySessionId,
        );
        return !hasInProgressSpan;
      },
    });

    const { persistStore = idbKeyvalPersistStore, ...restDeviceInput } = config?.deviceConfig || {};

    this.device = new Device({ persistStore, ...restDeviceInput });
    if (!isSSR) {
      this.device.init();
    }

    this.eventProcessor = new EventProcessor({
      storage: this.storage,
    });
    this.currentUser = new User();
  }

  private get currentActivitySessionId() {
    return this.activitySessionManager.currentActivitySession.activitySessionId;
  }

  start(viewParams?: ViewParameters) {
    if (isSSR && process.env.NODE_ENV !== 'test') {
      return;
    }
    this.activitySessionManager.start();
    this.dataTransferScheduler.start();
    this.viewManager.start(viewParams);
    this.logAction({
      data: { type: 'INITIAL_ROUTE', view: this.viewManager.currentView },
    });
  }

  stop() {
    this.activitySessionManager.stop();
    this.dataTransferScheduler.stop();
    this.viewManager.stop();
  }

  addEventAddedListener(cb: (event: Event) => void) {
    this.eventAddedListeners.push(cb);
  }

  removeEventAddedListener(cb: (event: Event) => void) {
    this.eventAddedListeners = this.eventAddedListeners.filter((c) => c !== cb);
  }

  startSpan(spanContext: SpanContext) {
    this.activitySessionManager.signaling();

    return this.eventCollector.startSpan({
      ...spanContext,
      activitySessionId: this.currentActivitySessionId,
      view: this.viewManager.currentView,
      userId: this.currentUser.userId,
    });
  }

  onEventEnd = async (event: Event) => {
    if (APP_SESSION_EVENTS.includes(event.data.type)) {
      const convertedEvent = await this.eventProcessor.convertEventToAppSession(event);
      if (convertedEvent) {
        this.storage.add(convertedEvent);
      }
    } else if (LRS_X_API_EVENTS.includes(<XApiActionType>event.data.type)) {
      this.storage.add(event.data as XApiActionData);
    } else {
      const convertedEvent = this.eventProcessor.convertEventToStudyEventLog(event);
      if (convertedEvent) {
        this.storage.add(convertedEvent);
      }
    }
    this.eventAddedListeners.forEach((cb) => cb(event));
  };

  logAction(actionContext: ActionContext) {
    if (!(actionContext.data.type === 'SESSION_EXPIRATION' || actionContext.data.type === 'SESSION_START')) {
      this.activitySessionManager.signaling();
    }

    let parentSpanId = actionContext.parentSpanId;

    if (actionContext.data.type === 'SUBMIT') {
      const studyActivityInstanceId = actionContext.data.studyActivityInstanceId;
      const timeSpentSpan = this.findSpan((span) => {
        return (
          span.data.type === 'STUDY_TIME_SPENT' &&
          span.data.studyActivityInstanceId === studyActivityInstanceId
        );
      });
      if (timeSpentSpan) {
        parentSpanId = timeSpentSpan.spanId;
      }
    }

    const action = this.eventCollector.createAction({
      ...actionContext,
      activitySessionId: this.currentActivitySessionId,
      view: this.viewManager.currentView,
      userId: this.currentUser.userId,
      parentSpanId,
    });
    return action;
  }

  startNewView(params: ViewParameters) {
    this.viewManager.startNewView(params);
  }

  setUserId(userId: string) {
    this.currentUser.setUserId(userId);
  }

  clearUser() {
    this.currentUser.clearUser();
  }

  findSpan(cb: (span: Span) => boolean) {
    return this.eventCollector.find(cb);
  }

  async studyTimeExchange() {
    this.logAction({
      data: { type: '__INTERNAL_STUDY_TIME_EXCHNAGE', internal: true },
    });
    await delay(10);
  }

  #handleViewChange = (view: View) => {
    this.eventCollector.onViewChange(view);
    this.logAction({
      data: { type: 'ROUTE_CHANGE', view },
    });
  };

  #onActivitySessionFinish = (session: ActivitySession) => {
    this.logAction({
      data: { type: 'SESSION_EXPIRATION' },
      activitySessionId: session.activitySessionId,
    });
  };

  #onActivitySessionStart = (session: ActivitySession) => {
    this.logAction({
      data: { type: 'SESSION_START' },
      activitySessionId: session.activitySessionId,
    });
  };

  #sendData = async () => {
    // device 준비 전에는 큐를 건드리지 않는다 — 준비되면 다음 틱에 그대로 전송된다.
    // (예전엔 drain 뒤 early-return하면서 items를 영영 못 보냈다)
    if (!this.device.isReady()) {
      return;
    }
    const device = this.device.toInputData();
    if (!device) {
      return;
    }
    this.storage.drain(async ({ items }) => {
      if (items?.length === 0) {
        return;
      }
      await this.transport.send({ studyEventLogs: items, device });
    });
  };
}
